/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import InputField from "@/components/Atoms/InputField";
import InputLabel from "@/components/Atoms/InputLabel";
import {useResponseId,useUrlType} from "@/components/hooks";
import {RootState} from "@/reducers/store";
import {useValidate} from "@/state/survey/formSlice";
import {BasicAnswerSave,useUpdateAnswersSubmit} from "@/state/survey/saveAnswerSlice";
import {Utility,Validate} from "@/utils";
import {constants} from "@/constants";
import {ReactNode,useEffect,useRef,useState} from "react";
import {useSelector} from "react-redux";
import poaAnswerDetails from "../../SurveyModel/poaQuestions/poaAnswerDetails";
import {IComponentMetaData} from "../../SurveyModel/types/commonTypes";
import {INode} from "../../SurveyModel/types/nodeTypes";

interface ISingleLineText {
    node: INode
    children: ReactNode
    name: string
    disabled: boolean
    metaData?: IComponentMetaData
    repeatedElement?:boolean
    onBlur?:()=> void
    type?:string
}

const SingleLineText:React.FC<ISingleLineText>  = (props:ISingleLineText) => {
    //repeatedElement - only available with complex question types with repeated nodes.
    const {node, children, name, disabled, repeatedElement, onBlur, type="Text" } = props;
    const form = useSelector((state:RootState) => state.form);
    const [questionName, setQuestionName] = useState('') 
    const validateInputField = useValidate();
    const updateAnswersDispatch = useUpdateAnswersSubmit();
    const {answerResponseId} = useResponseId();
    const hasExecuted = useRef(false);
    const { state = {} } = form[node.name];
    const [inputValue, setInputValue ] = useState(form[node.name].value);
    const {isPatient} = useUrlType();
    const [showAlert, setShowAlert] = useState(false);
    const allowDecimalConfig = node.configurations?.find((c: { key: string; value: string }) => c.key === constants.AllowDecimal);
    const allowDecimalPlaces = allowDecimalConfig ? parseInt(allowDecimalConfig.value, 10) : null;

    useEffect(()=>{
        if(form[node.name].value !== inputValue){
            setInputValue(form[node.name].value )
        }
    }, [form, inputValue]);
   
    const handleOnChange = (value: string, name: string) => {
        if (type === "Number" || node.inputType === "Number") {
            if (allowDecimalPlaces !== null) {
                if (allowDecimalPlaces === 0) {
                    // Only allow integers (no decimal point)
                    if (value !== "" && !constants.INTEGER_REGEX.test(value)) return;
                } else {
                    // Allow digits with up to allowDecimalPlaces decimal places
                    const decimalRegex = new RegExp(`^-?\\d*(\\.\\d{0,${allowDecimalPlaces}})?$`);
                    if (value !== "" && !decimalRegex.test(value)) return;
                }
            }
        }
        validateInputField(name, value, true);
        if(node.validationDependants) {
            node.validationDependants.forEach((element : string)=> {
                validateInputField(element, form[element].value, true);
            });
        }
    }
    
    const handleOnBlur = () => {
        if(!isPatient && answerResponseId && !repeatedElement) {
            const answers: BasicAnswerSave[] = poaAnswerDetails.generateQuestionAnswerSubmitFormat(form,node )
            updateAnswersDispatch(answers, answerResponseId);
        } 
        if(repeatedElement && onBlur) 
            onBlur()
    }

    useEffect(() => {
        node?.alerts?.map((alert: any) => {
            const shouldShowAlert = Utility.evaluateManually(form, alert.expression);
            if (shouldShowAlert) {
                setShowAlert(true);
            } else {
                setShowAlert(false);
            }
        });
    }, [node?.alerts, form]);

    useEffect(()=>{
        if(!isPatient && name && form[node.name] && questionName != name && !hasExecuted.current){ //poa only
            validateInputField(name, form[node.name].value, true);
            setQuestionName(name)
            hasExecuted.current = true;
        }
    }, [form, questionName, node, isPatient, name,validateInputField ]);

    return (
        <div className={ `single-line-text-wrap 
        ${Utility.getClassForWidthPx(node)} 
        ${node.name}
        ${!Utility.getClassForWidthPx(node) && !Utility.getClassForWidth(node) ? 'flex-1' : ''} ${!repeatedElement ? `user-input ${Utility.getClassForWidth(node)} 
        question-bottom-space-1
        ${!isPatient ? 'poa-input': '' }
         input-border-top` : ''}${showAlert ? ' red-strip' : ''}`}>

            <div className={`${Utility.getClassForWidth(node)} user-input-body`}>
                <InputLabel label={node.title} labelClasses={`input-label ${Utility.getClassesForLabel(node)}`}></InputLabel> 
                {node.subTitle  ? <InputLabel type="subTitle" label={node.subTitle } labelClasses="input-label"></InputLabel> : ''}
                {/* <SingleChoice propClasses={'single-chocie-choices flex'} value={form[node.name].value} options={node.options} name={node.name} onChange={handleChange}/> */}
                <InputField  disabled={disabled} form={form} decimalPlaces={allowDecimalConfig ? allowDecimalPlaces : undefined} isInValid = {Validate.isInvalid(state)} propClasses={'single-line-text flex'}  type={node.inputType ? node.inputType : type} value={form[node.name].value}  name={name} onChange={handleOnChange} onBlur={handleOnBlur} />
                <div>{children}</div>
            </div>
        </div>
    )
}

export default SingleLineText;